/**
 * The console's components, as functions that return markup.
 *
 * Every class name here is one the stylesheet already defines. Nothing invents
 * a style: if something needs to look different, the change belongs in the
 * stylesheet, where both pages can see it.
 */

import { esc } from "./dom";
import { t } from "./strings";
import type { Key } from "./strings";

/** Whole birr, grouped, with the currency in front. Never a decimal. */
export function money(amount: number): string {
  return `ETB ${Math.round(amount).toLocaleString("en-US")}`;
}

export function percent(share: number): string {
  return `${String(Math.round(share * 100))}%`;
}

/** A date a person can read, in the language they are reading the page in. */
export function on(date: Date | null): string {
  if (!date) {
    return "—";
  }
  return date.toLocaleDateString(t("common.language") === "English" ? "am-ET" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** The five states a unit can be in, plus the two a repair can. */
interface PillStyle {
  css: string;
  mark: string;
  label: Key;
}

/** A state the rest of the app has and this table has not heard of yet. */
const UNKNOWN: PillStyle = { css: "pill-held", mark: "", label: "state.inactive" };

const PILLS: Record<string, PillStyle> = {
  verified: { css: "pill-verified", mark: "✓", label: "state.verified" },
  unverified: { css: "pill-unverified", mark: "?", label: "state.unverified" },
  unpaid: { css: "pill-unpaid", mark: "!", label: "state.unpaid" },
  partial: { css: "pill-partial", mark: "½", label: "state.partial" },
  inactive: { css: "pill-held", mark: "–", label: "state.inactive" },
  // A complaint's five states, exactly as core defines them; any other name
  // draws as the unknown pill.
  reported: { css: "pill-open", mark: "", label: "repairs.reported" },
  assigned: { css: "pill-unverified", mark: "", label: "repairs.assigned" },
  attended: { css: "pill-partial", mark: "", label: "repairs.attended" },
  confirmed: { css: "pill-done", mark: "✓", label: "repairs.confirmed" },
  closed: { css: "pill-held", mark: "", label: "repairs.closed" },
};

/**
 * A state, said in a mark and a word.
 *
 * Roughly one man in twelve cannot separate the gold from the red, so nothing
 * here is said by colour alone: every pill carries its own word, and four of
 * them carry a mark as well.
 */
export function pill(state: string): string {
  const style = PILLS[state] ?? UNKNOWN;
  const mark = style.mark === "" ? "" : style.mark + " ";
  return `<span class="pill ${esc(style.css)}">${esc(mark + t(style.label))}</span>`;
}

/**
 * A name for something the database chose: an action in the change log, the
 * kind of a repair.
 *
 * The key is built here rather than at the call site so there is one place
 * that knows these are dynamic, and so a value the string table has not heard
 * of shows as itself rather than as "action.something.unknown".
 */
export function named(prefix: "action" | "category", value: string): string {
  const key = `${prefix}.${value}` as Key;
  const label = t(key);
  return label === key ? value : label;
}

export function card(body: string, extra = ""): string {
  return `<div class="card${extra === "" ? "" : ` ${extra}`}">${body}</div>`;
}

/** A figure with its label above it, which is most of what a console shows. */
export function stat(label: string, value: string, sub = "", extra = ""): string {
  return card(
    `<h3>${esc(label)}</h3><div class="stat-value">${esc(value)}</div>` +
      (sub === "" ? "" : `<div class="stat-sub">${esc(sub)}</div>`),
    extra,
  );
}

export function grid(columns: 2 | 3 | 4, cells: string[]): string {
  return `<div class="grid-${String(columns)}">${cells.join("")}</div>`;
}

export function sectionTitle(label: string): string {
  return `<h2 class="section-title">${esc(label)}</h2>`;
}

export function empty(label = t("common.nothing")): string {
  return `<div class="card empty">${esc(label)}</div>`;
}

export function bar(share: number): string {
  return `<div class="bar-track"><div class="bar-fill" style="width:${String(
    Math.max(0, Math.min(100, Math.round(share * 100))),
  )}%"></div></div>`;
}

/** A table in a card that can scroll sideways rather than squeeze its columns. */
export function table(headings: string[], rows: string[]): string {
  return card(
    `<div class="table-scroll" tabindex="0" role="region" aria-label="${esc(headings.filter(Boolean).join(", "))}"><table class="table"><thead><tr>${headings
      .map((heading) => `<th>${esc(heading)}</th>`)
      .join("")}</tr></thead><tbody>${rows.join("")}</tbody></table></div>`,
    "table-card",
  );
}

export function row(left: string, sub: string, right: string): string {
  return (
    `<div class="row"><div class="left"><strong>${esc(left)}</strong>` +
    (sub === "" ? "" : `<span>${esc(sub)}</span>`) +
    `</div>${right}</div>`
  );
}

export function rowList(rows: string[]): string {
  return `<div class="row-list">${rows.join("")}</div>`;
}

export function field(id: string, label: string, attrs = ""): string {
  return `<div><label for="${esc(id)}">${esc(label)}</label><input class="field" id="${esc(
    id,
  )}" ${attrs} /></div>`;
}

export function gold(id: string, label: string): string {
  return `<button type="button" class="btn btn-gold" id="${esc(id)}">${esc(label)}</button>`;
}

/* --------------------------------------------------------- skeletons ---- */

/**
 * The shape of a view before its figures arrive.
 *
 * These exist so a screen never opens empty and never jumps. Each one is built
 * from the same grid and card the real view uses, which is the only way the
 * grey boxes end up the size of the thing replacing them. If a view's layout
 * changes and its skeleton does not, the jump comes back, so they are kept
 * next to each other here rather than in the view.
 */
function skel(className: string, style = ""): string {
  return `<span class="skel ${className}"${style === "" ? "" : ` style="${style}"`}></span>`;
}

/** A stat card: its heading, its figure, its note. */
export function skeletonStat(): string {
  return card(skel("skel-head") + skel("skel-value") + skel("skel-line", "width:75%"));
}

/** A run of stat cards across the top, the way every home view opens. */
export function skeletonStats(columns: 2 | 3 | 4): string {
  return grid(
    columns,
    Array.from({ length: columns }, () => skeletonStat()),
  );
}

/**
 * A table, at the height it will really be.
 *
 * The row count is the caller's guess at what is coming. It is better slightly
 * high than low: a skeleton that is shorter than its answer pushes the page
 * down when the answer lands, which is the jump these are here to stop.
 */
export function skeletonTable(columns: number, rows = 5): string {
  const head = `<tr>${"<th>" + skel("skel-head", "width:4rem") + "</th>"}`.concat(
    `<th>${skel("skel-head", "width:4rem")}</th>`.repeat(columns - 1),
    "</tr>",
  );
  const body = `<tr>${`<td>${skel("skel-line", "width:80%;margin:0")}</td>`.repeat(columns)}</tr>`;
  return card(
    `<div class="table-scroll"><table class="table"><thead>${head}</thead><tbody>${body.repeat(rows)}</tbody></table></div>`,
    "table-card",
  );
}

/** A card of stacked rows: notices, the journal, a resident's months. */
export function skeletonRows(rows = 4): string {
  const one =
    `<div class="row"><div class="left">${skel("skel-line", "width:7rem;margin:0")}` +
    `${skel("skel-line", "width:4rem;margin-top:0.35rem")}</div>${skel("skel-pill")}</div>`;
  return card(`<div class="row-list">${one.repeat(rows)}</div>`);
}

/** A heading above a section, so the section titles do not pop in either. */
export function skeletonTitle(): string {
  return `<h2 class="section-title">${skel("skel-line", "width:9rem;height:1rem;margin:0")}</h2>`;
}

/**
 * Wraps a skeleton so assistive technology skips it.
 *
 * The boxes say nothing worth reading out. The region that will hold the real
 * view carries aria-busy, which is the part actually worth announcing.
 */
export function skeleton(body: string): string {
  return `<div class="skel-wrap" aria-hidden="true">${body}</div>`;
}
